import { Box, Button, Divider, Stack, Typography, alpha } from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import CheckInAnswersButton from 'components/molecules/myTeam/CheckInAnswersPopup'
import CompactRadarGraph from 'components/molecules/myTeam/CompactRadarGraph'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CHECK_IN_SURVEY } from 'routes/constants'
import colors from 'styles/colors'
import radarGraph from 'assets/images/radarGraph.svg'
import moment from 'moment'
import { formatDateTime, formatDifference } from 'utils/formatDateTime'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useState } from 'react'
import { AccordionWithBorder } from 'components/atoms/container/Accordion'
import { NotificationContainer } from 'components/atoms/Text'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { IconContainer } from 'components/atoms/Icons'

const MessageBox = ({ children, sx }) => (
  <BoxWithBackground
    backgroundColor={colors.Peach100}
    sx={{ padding: 8, width: '90%', ...sx }}>
    <InCardStack spacing={3} textAlign='center'>
      {children}
    </InCardStack>
  </BoxWithBackground>
)
const BackgroundBox = ({ children }) => (
  <Box
    sx={{
      backgroundImage: `url(${radarGraph})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      width: '100%',
      height: 320
    }}>
    <Box
      display='flex'
      height='100%'
      width='100%'
      justifyContent='center'
      alignItems='center'
      sx={{
        backgroundColor: alpha(colors.White, 0.9),
        backdropFilter: 'blur(2.5px)'
      }}>
      <MessageBox>{children}</MessageBox>
    </Box>
  </Box>
)

const CheckInSurveyResult = ({ userCalendarEvent, isOwner, surveyUser }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(true)
  const hasCurrentData = userCalendarEvent?.surveyDisplayData?.currentAverage
  const hasPreviousData = userCalendarEvent?.surveyDisplayData?.previousAverage
  const hasGraphData =
    userCalendarEvent?.surveyDisplayData?.currentAverage ||
    userCalendarEvent?.surveyDisplayData?.previousAverage
  const showGraph =
    hasGraphData && (userCalendarEvent?.isClosed || isOwner || hasCurrentData)
  const hasSurveyData = showGraph || (!isOwner && !userCalendarEvent?.isClosed)
  const surveyOpenTime = moment
    .utc(userCalendarEvent?.startTime)
    .subtract(1, 'day')
    .local()

  const scoreDifference =
    hasCurrentData &&
    hasPreviousData &&
    (
      userCalendarEvent.surveyDisplayData.currentAverage -
      userCalendarEvent.surveyDisplayData.previousAverage
    ).toFixed(1)

  const hasDifference = scoreDifference && scoreDifference !== 0

  const CurrentAverage = ({ typoProps = {} }) => {
    return (
      <RowStack spacing={2} width={'auto'}>
        <Typography variant='h1' lineHeight='100%' {...typoProps}>
          {hasCurrentData
            ? userCalendarEvent.surveyDisplayData.currentAverage.toFixed(2)
            : 'N/A'}
        </Typography>
        {hasDifference && (
          <BoxWithBackground
            backgroundColor={
              scoreDifference > 0 ? colors.Green200 : colors.SecondaryLightRed
            }
            minWidth={40}
            padding={2}
            paddingLeft={1}
            paddingRight={3}
            borderRadius={3}>
            <RowStack spacing={0}>
              {scoreDifference > 0 ? (
                <ArrowDropUpIcon fontSize='small' />
              ) : (
                <ArrowDropDownIcon fontSize='small' />
              )}
              <Typography variant='body1Small' marginLeft={-1}>
                {hasDifference ? Math.abs(scoreDifference) : '-'}
              </Typography>
            </RowStack>
          </BoxWithBackground>
        )}
      </RowStack>
    )
  }

  const getSummaryComponent = () => {
    if (hasCurrentData) {
      if (expanded) {
        return (
          <CheckInAnswersButton
            userCalendarEvent={userCalendarEvent}
            surveyUser={surveyUser}
          />
        )
      } else {
        return (
          <CurrentAverage
            typoProps={{
              fontSize: 28,
              fontWeight: 500,
              lineHeight: '120%'
            }}
          />
        )
      }
    } else if (!hasSurveyData) {
      return (
        <NotificationContainer>
          <Typography fontSize={13} fontWeight={500} lineHeight={'150%'}>
            {t('checkInSurvey.surveyDataUnavailable')}
          </Typography>
          <IconContainer icon={<InfoOutlinedIcon />} />
        </NotificationContainer>
      )
    }
    return null
  }

  return (
    <AccordionWithBorder
      summaryTitle={t('checkInSurvey.latestSurvey')}
      summaryComponent={getSummaryComponent()}
      expanded={hasSurveyData ? expanded : false}
      setExpanded={hasSurveyData ? setExpanded : null}
      borderRadius={2}>
      <InCardStack spacing={0}>
        {showGraph ? (
          <>
            {(isOwner || userCalendarEvent?.isClosed) && !hasCurrentData && (
              <MessageBox sx={{ marginTop: 5 }}>
                <Typography textAlign='center'>
                  {t('checkInSurvey.thisWeekNotComplete')}
                </Typography>
              </MessageBox>
            )}
            <CompactRadarGraph
              assessmentData={userCalendarEvent.surveyDisplayData}
            />
            <BoxWithBackground paddingX={8} paddingY={4} paddingTop={6}>
              <RowStack>
                <Stack padding={0}>
                  <CurrentAverage />
                  <Typography variant='body1Micro' color={colors.Grey300}>
                    {t('checkInSurvey.current')}
                  </Typography>
                </Stack>
                <Divider orientation='vertical' flexItem />
                <InCardStack spacing={0} alignItems='flex-end'>
                  <Typography
                    variant='h2'
                    lineHeight='100%'
                    color={colors.Grey350}>
                    {hasPreviousData
                      ? userCalendarEvent.surveyDisplayData.previousAverage.toFixed(
                          2
                        )
                      : 'N/A'}
                  </Typography>
                  <Typography variant='body1Micro' color={colors.Grey300}>
                    {t('checkInSurvey.pastLabel', {
                      count: userCalendarEvent.surveyDisplayData.previousCount
                    })}
                  </Typography>
                </InCardStack>
              </RowStack>
            </BoxWithBackground>
          </>
        ) : (
          <BackgroundBox>
            {!isOwner && !userCalendarEvent?.isClosed ? (
              <>
                {surveyOpenTime.isSameOrBefore(moment.utc()) ? (
                  <>
                    <Typography>{t('checkInSurvey.surveyDueIn')}</Typography>
                    <Typography variant='h4'>
                      {formatDifference(
                        moment.utc(),
                        moment.utc(userCalendarEvent?.endTime)
                      )}
                    </Typography>
                    <Button
                      color='primary'
                      onClick={() =>
                        navigate(CHECK_IN_SURVEY, {
                          state: {
                            userCalendarEventId: userCalendarEvent?.id,
                            fromMeeting: true
                          }
                        })
                      }>
                      {t('checkInSurvey.completeSurveyNow')}
                    </Button>
                  </>
                ) : surveyOpenTime.isAfter(moment.utc()) ? (
                  <>
                    <Typography>
                      {t('checkInSurvey.nextSurveyOpens')}
                    </Typography>
                    <Typography variant='h4'>
                      {formatDateTime(surveyOpenTime)}
                    </Typography>
                  </>
                ) : (
                  <Typography>
                    {t('checkInSurvey.surveyNotAvailable')}
                  </Typography>
                )}
              </>
            ) : null}
          </BackgroundBox>
        )}
      </InCardStack>
    </AccordionWithBorder>
  )
}

export default CheckInSurveyResult
